// src/features/system/developer/components/video-gallery/VideoFeed.tsx
// ────────────────────────────────────────────────────────────────────────────
// Social-media style vertical video feed (TikTok / Reels parity).
//
// Design choices:
//   • Snap-scroll vertical container (CSS `scroll-snap-type: y mandatory`).
//   • Each item fills the viewport — one video, fully focused.
//   • IntersectionObserver gates autoplay: the video whose tile is ≥75%
//     visible plays; everyone else pauses. Hard guarantee: only one
//     element calls `.play()` at a time.
//   • Progressive playback: signed URL is minted lazily (single-flight,
//     same cache the modal uses), `<video preload="auto">` so range
//     requests stream as the buffer fills.
//   • Infinite scroll: walks the entire `visibleItems` set chunked the
//     same way the grid does — feed never out-fetches the gallery.
//   • Owns NO state of its own; everything comes from `useVideoGallery`.
// ────────────────────────────────────────────────────────────────────────────
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseVideoGalleryReturn } from './useVideoGallery';
import type { VideoFeedItem } from './types';
import { PlaybackNotReadyError } from './api';
import { formatBytes, formatDuration, statusTone } from './format';
import LiveTimeAgo from './LiveTimeAgo';
import { MapPinLink } from './MapPinLink';
import ShareLinkButton from './ShareLinkButton';
import { presignUpcoming, rangePrewarmNext, cancelRangePrewarm } from './prewarm';
import { addVideoHint, removeVideoHint, clearAllVideoHints } from './resourceHints';
import { notifyStall } from './bandwidthEstimator';
import { VideoFeedErrorBoundary } from './VideoFeedErrorBoundary';
import { useWakeLock } from './hooks/useWakeLock';
import { useMediaSession } from './hooks/useMediaSession';
import { emitFeedEvent } from './telemetry';

// ── Not-ready retry policy (parity with poster/preview lazy-heal) ─────
// When `/playback-url` returns a typed `PlaybackNotReadyError` it means
// the row exists but its GCS object is not materialised yet (in-flight
// resumable PUT, mid-fallback resolution, or pre-fix legacy row). The
// cure is "wait & retry"; never "show a red error". This schedule mimics
// the bounded jittered backoff used by the poster lazy-heal loop:
//
//   attempt 1: ~ 2.5 s   (catches the typical 1–3 s resumable PUT tail)
//   attempt 2: ~ 6 s
//   attempt 3: ~ 12 s
//   attempt 4: ~ 24 s
//   attempt 5: ~ 48 s
//   attempt 6: ~ 96 s
//   attempt 7: ~ 180 s   (terminal — stop hammering, leave card idle)
//
// All delays get ±25 % jitter applied at scheduling time to spread
// thundering-herd retries across a feed where many cards entered the
// not-ready state in the same scroll tick.
const PLAYBACK_NOT_READY_BACKOFF_MS = [2_500, 6_000, 12_000, 24_000, 48_000, 96_000, 180_000] as const;
const PLAYBACK_NOT_READY_MAX_ATTEMPTS = PLAYBACK_NOT_READY_BACKOFF_MS.length;
function nextPlaybackRetryDelay(attempt: number, serverHintMs?: number): number {
  const base = serverHintMs && serverHintMs > 0
    ? serverHintMs
    : PLAYBACK_NOT_READY_BACKOFF_MS[Math.min(attempt, PLAYBACK_NOT_READY_BACKOFF_MS.length - 1)];
  const jitter = (Math.random() * 0.5 - 0.25) * base; // ±25 %
  return Math.max(500, Math.round(base + jitter));
}
function humanizePlaybackNotReady(code: PlaybackNotReadyError['code']): string {
  switch (code) {
    case 'playback_not_ready_upload_identity':
      return 'Upload is finishing on the server';
    case 'playback_not_found':
      return 'Finalising in this tenant';
    case 'playback_not_ready':
    default:
      return 'Processing';
  }
}

// ── Mute preference (session-persisted) ────────────────────────────────
// Default is SOUND ON. The user explicitly asked for activated audio
// by default — matching the TikTok/Shorts/IG "tap-to-unmute persists"
// pattern, except we start from unmuted instead of muted on cold load.
//
// Browser autoplay policy still requires user activation before unmuted
// playback. We honor that defensively: if the initial `.play()` is
// rejected by policy, the slide auto-falls back to `muted = true` and
// surfaces a visible "Tap to unmute" affordance. The persisted
// preference is never changed by the policy-fallback path — only an
// explicit user toggle writes localStorage.
const MUTE_PREF_KEY = 'pac:videofeed:muted';
function readMutedPref(): boolean {
  try {
    const v = window.localStorage?.getItem(MUTE_PREF_KEY);
    if (v === '1') return true;
    if (v === '0') return false;
    return false; // default: SOUND ON
  } catch {
    return false;
  }
}
function writeMutedPref(next: boolean): void {
  try { window.localStorage?.setItem(MUTE_PREF_KEY, next ? '1' : '0'); } catch { /* private mode / SSR */ }
}

interface Props {
  v: UseVideoGalleryReturn;
  onRequestDelete: (item: VideoFeedItem) => void;
  /**
   * When true, every destructive / write-side affordance (delete
   * action button, orphan-dismiss CTA) is hidden. Used by the public
   * `/s/:token` shared viewer where only read + download are allowed.
   */
  readOnly?: boolean;
  /** Controls whether the per-slide download action is shown. */
  canDownload?: boolean;
  /** When true, hides feed chrome (header-side actions/buttons/cards UX). */
  minimalChrome?: boolean;
}

const VISIBLE_CHUNK = 12;
const CHUNK_STEP    = 12;

export default function VideoFeed(props: Props) {
  // Outer error boundary: any uncaught throw from the feed renders the
  // recovery card instead of white-screening the parent dashboard.
  return (
    <VideoFeedErrorBoundary
      onError={(error) =>
        emitFeedEvent({
          kind: 'error',
          detail: { source: 'render', message: error.message },
        })
      }
    >
      <VideoFeedInner {...props} />
    </VideoFeedErrorBoundary>
  );
}

function VideoFeedInner({ v, onRequestDelete, readOnly = false, canDownload = true, minimalChrome = false }: Props) {
  const items = v.visibleItems;
  const [visibleCount, setVisibleCount] = useState(VISIBLE_CHUNK);
  const [activeIndex,  setActiveIndex]  = useState(0);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Reset chunk + scroll when the filtered set changes.
  useEffect(() => {
    setVisibleCount(VISIBLE_CHUNK);
    setActiveIndex(0);
    if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
  }, [v.activeBucketKey, v.searchText, v.includeInitiated, items.length]);

  // Infinite scroll sentinel.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (visibleCount >= items.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleCount((c) => Math.min(c + CHUNK_STEP, items.length));
        }
      },
      { root: scrollerRef.current, rootMargin: '600px 0px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [visibleCount, items.length]);

  const sliced = useMemo(() => items.slice(0, visibleCount), [items, visibleCount]);

  // ── Buffer-warm pipeline (YouTube-Shorts parity) ───────────────────────
  // Stage 1: pre-sign playback URLs for N+1, N+2, N+3 so a swipe finds the
  // URL already minted. Stage 2: range-prewarm the leading bytes of N+1 so
  // the <video> element starts playing without going through "Loading…".
  // Both stages are single-flight + network-aware (see `prewarm.ts`).
  //
  // Stage 3 (resource hints): a <link rel="preload" as="fetch"
  // fetchpriority="high"> for N+1's signed URL warms up the GCS socket
  // and gets the next slide's first range placed at the front of the
  // browser's H/2 priority queue. See `resourceHints.ts`.
  useEffect(() => {
    if (!items.length) return;
    presignUpcoming(items, activeIndex, v.requestPlayback, 3);
    rangePrewarmNext(items, activeIndex, v.requestPlayback);
    // Cancel pre-warms for slides we've scrolled past — they'll never
    // play, so don't waste bandwidth pulling their bytes.
    const stale = items[activeIndex - 2];
    if (stale) cancelRangePrewarm(stale.video_id);

    // Stage 3 — resource hint for N+1. We only know the signed URL after
    // the (cached) requestPlayback resolves; fetch lazily and hint.
    const next = items[activeIndex + 1];
    if (next && next.error !== 'upload_orphan' && next.error !== 'metadata_unavailable') {
      let cancelled = false;
      let hintedUrl: string | null = null;
      v.requestPlayback(next.video_id)
        .then((res) => {
          if (cancelled || !res?.playback_url) return;
          hintedUrl = res.playback_url;
          addVideoHint(hintedUrl);
        })
        .catch(() => { /* presign failures are non-fatal here */ });
      return () => {
        cancelled = true;
        if (hintedUrl) removeVideoHint(hintedUrl);
      };
    }
    return undefined;
  }, [activeIndex, items, v.requestPlayback]);

  // ── Cleanup: cancel every in-flight prewarm when the feed unmounts or
  // ── the filter set changes — zombie range fetches waste bandwidth and
  // ── can race against the new active slide on slow links.
  useEffect(() => {
    return () => {
      for (const it of items) cancelRangePrewarm(it.video_id);
      clearAllVideoHints();
    };
  }, [items]);

  // ── Keyboard navigation (WCAG keyboard parity + power users) ────────────
  // ↓ / J → next slide   ↑ / K → previous slide
  // Space  → play/pause active slide (handled by FeedSlide click handler;
  //          we forward by emitting a synthetic click on the active video).
  // m      → toggle mute (event-bus pattern via custom event — keeps the
  //          FeedSlide self-contained and avoids prop-drilling another
  //          handler).
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    // Don't hijack typing inside form fields nested in the feed (none
    // today, but defensive against future overlays).
    const target = e.target as HTMLElement;
    const tag = target?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;

    const scroller = scrollerRef.current;
    if (!scroller) return;

    const scrollToIdx = (idx: number) => {
      const clamped = Math.max(0, Math.min(idx, items.length - 1));
      const node = scroller.children[clamped] as HTMLElement | undefined;
      if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    switch (e.key) {
      case 'ArrowDown':
      case 'j':
      case 'J':
        e.preventDefault();
        emitFeedEvent({ kind: 'keyboard_nav', detail: { key: 'next' } });
        scrollToIdx(activeIndex + 1);
        break;
      case 'ArrowUp':
      case 'k':
      case 'K':
        e.preventDefault();
        emitFeedEvent({ kind: 'keyboard_nav', detail: { key: 'prev' } });
        scrollToIdx(activeIndex - 1);
        break;
      case ' ':
      case 'Spacebar':
        e.preventDefault();
        emitFeedEvent({ kind: 'keyboard_nav', detail: { key: 'space' } });
        window.dispatchEvent(new CustomEvent('video-feed:toggle-play'));
        break;
      case 'm':
      case 'M':
        e.preventDefault();
        emitFeedEvent({ kind: 'mute_toggle', detail: { source: 'keyboard' } });
        window.dispatchEvent(new CustomEvent('video-feed:toggle-mute'));
        break;
    }
  }, [activeIndex, items.length]);

  // ── Adaptive preload strategy ───────────────────────────────────
  // Only the active slide and the immediately-next one buffer aggressively.
  // Every other slide preloads metadata only so we don't burn bandwidth or
  // sockets on slides the user may never reach.
  const preloadFor = useCallback((idx: number): 'auto' | 'metadata' => {
    return (idx === activeIndex || idx === activeIndex + 1) ? 'auto' : 'metadata';
  }, [activeIndex]);

  // ── Slide-view telemetry ────────────────────────────────────────
  // Sampled (10% by default in telemetry.ts) so we capture browsing
  // patterns without flooding the analytics sink on long feeds.
  useEffect(() => {
    const active = items[activeIndex];
    if (!active) return;
    emitFeedEvent({
      kind: 'slide_view',
      video_id: active.video_id,
      index: activeIndex,
      detail: {
        status: active.status,
        bucket: v.activeBucketKey ?? null,
      },
    });
  }, [activeIndex, items, v.activeBucketKey]);

  if (!items.length) {
    return (
      <div className="h-full min-h-[20rem] flex items-center justify-center text-neutral-500 text-sm">
        Nothing to play yet — try clearing filters or uploading from PACMobile.
      </div>
    );
  }

  return (
    <div
      ref={scrollerRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      role="region"
      aria-label="Video feed — use arrow keys or J/K to navigate, Space to play, M to mute"
      className="h-full min-h-0 overflow-y-auto snap-y snap-mandatory bg-black rounded-xl
                 focus:outline-none focus-visible:ring-1 focus-visible:ring-neon_orange-300/40
                 scrollbar-thin scrollbar-thumb-neutral-700 scrollbar-track-transparent"
    >
      {sliced.map((it, idx) => (
        <FeedSlide
          key={it.video_id}
          item={it}
          index={idx}
          isActive={idx === activeIndex}
          onBecameActive={() => setActiveIndex(idx)}
          requestPlayback={v.requestPlayback}
          requestPoster={v.requestPoster}
          onRequestDelete={onRequestDelete}
          deleting={v.pendingDeletes.has(it.video_id)}
          preload={preloadFor(idx)}
          readOnly={readOnly}
          canDownload={canDownload}
          minimalChrome={minimalChrome}
        />
      ))}

      {visibleCount < items.length && (
        <div ref={sentinelRef} className="h-24 flex items-center justify-center text-[11px] text-neutral-500">
          <span className="animate-pulse">Loading more…</span>
        </div>
      )}
    </div>
  );
}

// ── Slide ───────────────────────────────────────────────────────────────

interface SlideProps {
  item: VideoFeedItem;
  index: number;
  isActive: boolean;
  onBecameActive: () => void;
  requestPlayback: UseVideoGalleryReturn['requestPlayback'];
  /**
   * Optional poster resolver. When provided, the slide fetches the
   * signed JPEG sidecar URL and forwards it as the `<video poster>`
   * attribute so the first paint is the poster frame instead of a
   * black tile while the video bytes load. TikTok / YT Shorts / IG
   * Reels parity. Gracefully degrades when poster is missing.
   */
  requestPoster?: UseVideoGalleryReturn['requestPoster'];
  onRequestDelete: (item: VideoFeedItem) => void;
  deleting: boolean;
  /**
   * Adaptive preload hint forwarded to <video preload>. Active + N+1 use
   * 'auto' (aggressive buffer); everyone else uses 'metadata' to keep
   * speculative bandwidth and socket count bounded.
   */
  preload: 'auto' | 'metadata';
  /** Hides destructive affordances; surfaces Download action. */
  readOnly?: boolean;
  /** Controls whether the download action is rendered. */
  canDownload?: boolean;
  /** Hides non-essential overlay chrome in single-video public mode. */
  minimalChrome?: boolean;
}

function FeedSlideImpl({
  item, index, isActive, onBecameActive, requestPlayback, requestPoster, onRequestDelete, deleting, preload, readOnly = false, canDownload = true, minimalChrome = false,
}: SlideProps) {
  const isTouchDevice = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return Boolean(window.matchMedia?.('(pointer: coarse)').matches || 'ontouchstart' in window);
  }, []);
  const slideRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState('video/mp4');
  // ── Poster sidecar (TikTok/Shorts/IG parity) ───────────────────
  // The signed JPEG poster URL, used as the `<video poster>` attribute
  // so the first paint is the poster frame (not a black tile) while
  // the video bytes load. Best-effort: when the sidecar isn't yet
  // generated, the BFF returns 202 + retry hint and the hook layer
  // self-heals via Pub/Sub dispatch + jittered retry loop. We pick
  // up the eventual URL via the `pac:poster:resolved` window event.
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  const [error,       setError]       = useState<string | null>(null);
  // ── Not-ready state (transient, NOT an error) ──────────────────
  // Populated when `requestPlayback` throws `PlaybackNotReadyError`.
  // Renders a "Processing…" overlay (NOT "Cannot load video") so the
  // user understands the asset is still finishing on the server side.
  // Drives the auto-retry timer below.
  const [notReady, setNotReady] = useState<{
    code: PlaybackNotReadyError['code'];
    attempt: number;
    nextRetryAtMs: number;
  } | null>(null);
  const notReadyRetryTimerRef = useRef<number | null>(null);
  const [muted,       setMuted]       = useState<boolean>(() => (minimalChrome ? false : readMutedPref()));
  const [buffering,   setBuffering]   = useState(false);
  const [bufferedPct, setBufferedPct] = useState(0);
  const [currentTimeSec, setCurrentTimeSec] = useState(0);
  const [durationSec, setDurationSec] = useState(0);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [playing,     setPlaying]     = useState(false);
  const [signing,     setSigning]     = useState(false);
  const [pinchScale, setPinchScale] = useState(1);
  const [pinchActive, setPinchActive] = useState(false);
  const pinchDistanceRef = useRef<number | null>(null);
  const pinchScaleStartRef = useRef(1);
  const pinchScaleRef = useRef(1);
  const ignoreTapUntilRef = useRef(0);
  const suppressOverlayUntilRef = useRef(0);
  // ── Mobile controls overlay (TikTok/IG/Shorts parity) ──────────
  // On touch devices we render our OWN media chrome (center play/pause +
  // seek bar + time) instead of the browser's native controls. A single
  // tap on the surface toggles the overlay; it auto-hides after a short
  // idle window so the content stays immersive. Tapping the video NEVER
  // pauses directly — play/pause lives on the explicit center button
  // (the Shorts/Reels pattern users expect).
  const [controlsVisible, setControlsVisible] = useState(false);
  const controlsHideTimerRef = useRef<number | null>(null);
  const lastTapTsRef = useRef(0);
  const tapSingleTimerRef = useRef<number | null>(null);
  const seekHintTimerRef = useRef<number | null>(null);
  const [seekHint, setSeekHint] = useState<null | { side: 'left' | 'right'; delta: number }>(null);
  // Auto-recovery counter: bounded to 1 retry to avoid infinite loops on
  // genuinely broken assets. A single transient retry handles 99% of
  // signed-URL races and momentary network blips invisibly.
  const recoveryAttemptsRef = useRef(0);
  const tone = statusTone(item.status);

  // ── Wake Lock: hold the screen on while this slide is active + playing
  useWakeLock(isActive && playing && !error);

  // ── OS-level media controls (lock screen, control center, Bluetooth)
  useMediaSession({
    isActive,
    videoEl: videoRef.current,
    title: item.display_name ?? item.scope_key ?? 'Video',
    onPlay: () => {
      const el = videoRef.current;
      if (el && playbackUrl) void el.play().catch(() => {});
    },
    onPause: () => {
      const el = videoRef.current;
      if (el && !el.paused) el.pause();
    },
  });

  // Mark this slide active when ≥75% of it is in view. The parent
  // single-flights `play()` based on that signal — only one video
  // can be active at a time.
  useEffect(() => {
    const el = slideRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio >= 0.75) {
            onBecameActive();
          }
        }
      },
      { threshold: [0, 0.5, 0.75, 1] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [onBecameActive]);

  // Lazy-mint the signed URL only when the slide is (or is about to be)
  // active. Saves 1 HTTP call per off-screen slide and respects the
  // server's least-privilege TTL.
  const ensurePlayback = useCallback(async () => {
    if (playbackUrl || signing || error) return;
    setSigning(true);
    try {
      const res = await requestPlayback(item.video_id);
      setPlaybackUrl(res.playback_url);
      setContentType(res.content_type || 'video/mp4');
      setNotReady(null);
    } catch (err) {
      if (err instanceof PlaybackNotReadyError) {
        // Not an error — the asset is materialising. Schedule a bounded
        // jittered retry; render "Processing…" until it heals or hits
        // the cap.
        setNotReady((prev) => {
          const attempt = (prev?.attempt ?? 0) + 1;
          if (attempt > PLAYBACK_NOT_READY_MAX_ATTEMPTS) {
            // Bounded — give up scheduling further retries, leave the
            // "Processing…" overlay so the user understands state.
            return { code: err.code, attempt: PLAYBACK_NOT_READY_MAX_ATTEMPTS, nextRetryAtMs: 0 };
          }
          const delayMs = nextPlaybackRetryDelay(attempt - 1, err.retryAfterMs);
          if (notReadyRetryTimerRef.current !== null) {
            window.clearTimeout(notReadyRetryTimerRef.current);
          }
          notReadyRetryTimerRef.current = window.setTimeout(() => {
            notReadyRetryTimerRef.current = null;
            // The hook-level negative cache TTL is shorter than the
            // shortest retry interval, so this fetch will hit the BFF.
            void ensurePlayback();
          }, delayMs);
          return { code: err.code, attempt, nextRetryAtMs: Date.now() + delayMs };
        });
      } else {
        setError((err as Error)?.message || 'Failed to load playback url');
      }
    } finally {
      setSigning(false);
    }
  }, [playbackUrl, signing, error, requestPlayback, item.video_id]);

  // Clear retry timer on unmount / video change to avoid leaks +
  // stale closures firing against an unmounted slide.
  useEffect(() => {
    return () => {
      if (notReadyRetryTimerRef.current !== null) {
        window.clearTimeout(notReadyRetryTimerRef.current);
        notReadyRetryTimerRef.current = null;
      }
    };
  }, [item.video_id]);

  // ── Poster fetch (active + adjacent slides) ────────────────────
  // Mirror the same adjacency rule as `preload` (active/N+1 get auto,
  // others get metadata) — we only need the poster for slides the
  // user is likely to see imminently. The hook layer single-flights
  // + caches + retries, so re-fires are cheap. Failure / 202 keeps
  // posterUrl null; the slide still renders skeleton + plays video
  // once playbackUrl resolves.
  useEffect(() => {
    if (!requestPoster) return;
    if (preload !== 'auto') return; // off-screen: skip
    let cancelled = false;
    requestPoster(item.video_id)
      .then((r) => {
        if (!cancelled && r?.poster_url) setPosterUrl(r.poster_url);
      })
      .catch(() => { /* posterless slide is acceptable */ });
    return () => { cancelled = true; };
  }, [requestPoster, item.video_id, preload]);

  // ── Listen for hook background-retry resolution ────────────────
  // When the BFF's lazy-heal pipeline eventually delivers the poster,
  // the hook fires a window CustomEvent. We rebind without needing a
  // react-query subscription per slide.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResolved = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      if (detail.videoId === item.video_id && typeof detail.posterUrl === 'string') {
        setPosterUrl(detail.posterUrl);
      }
    };
    window.addEventListener('pac:poster:resolved', onResolved as EventListener);
    return () => window.removeEventListener('pac:poster:resolved', onResolved as EventListener);
  }, [item.video_id]);

  // Drive autoplay / pause based on whether this slide is the active one.
  //
  // Buffer-aware play gate (commercial-player parity):
  //   • If readyState >= HAVE_FUTURE_DATA (3), play immediately — we have
  //     enough buffered to advance at least one frame without re-buffer.
  //   • Otherwise, wait for `canplay` (HAVE_FUTURE_DATA) up to 600 ms,
  //     then play regardless. 600 ms is the perceptual upper bound that
  //     still feels "instant" to the user (Nielsen / Google RAIL budget).
  //   • This eliminates the visible "play → waiting" flash that occurs
  //     when we call play() against an element that hasn't received its
  //     first segment yet.
  //
  // Autoplay-policy fallback (TikTok/Shorts/IG parity):
  //   If the user prefers SOUND ON (muted = false) and the browser
  //   rejects unmuted autoplay (NotAllowedError), we transparently flip
  //   the element to muted and retry. The user's persisted preference
  //   is NOT changed — once they tap unmute (or any other gesture
  //   satisfies user-activation), the next slide plays with sound.
  const playWithAutoplayFallback = useCallback((el: HTMLVideoElement) => {
    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch((err: unknown) => {
        const name = (err as { name?: string } | null)?.name || '';
        // NotAllowedError = browser autoplay policy. AbortError = the
        // element was re-targeted (e.g. we paused before play() resolved);
        // both are non-fatal and should not surface to the user.
        if (name === 'NotAllowedError' && !el.muted) {
          // Guarantee playback start: if unmuted autoplay is blocked,
          // retry in muted mode so the video does not remain paused.
          el.muted = true;
          // Reflect the runtime fallback in component state so the speaker
          // icon + overlay match reality. localStorage is intentionally
          // NOT touched here — fallback ≠ user intent.
          setMuted(true);
          setControlsVisible(true);
          el.play().catch(() => { /* even muted autoplay can be blocked on Lockdown Mode etc. */ });
        }
      });
    }
  }, []);

  // Single-video shared links must always default to Sound ON.
  useEffect(() => {
    if (!minimalChrome) return;
    setMuted(false);
    writeMutedPref(false);
    const el = videoRef.current;
    if (el) el.muted = false;
  }, [minimalChrome, playbackUrl]);

  useEffect(() => {
    if (isActive) {
      ensurePlayback();
      const el = videoRef.current;
      if (!el || !playbackUrl) return;

      // HAVE_FUTURE_DATA = 3 — enough buffered to advance.
      if (el.readyState >= 3) {
        playWithAutoplayFallback(el);
        return;
      }

      let played = false;
      let timer: ReturnType<typeof setTimeout> | null = null;
      const tryPlay = () => {
        if (played) return;
        played = true;
        playWithAutoplayFallback(el);
      };
      const onReady = () => tryPlay();
      el.addEventListener('canplay', onReady, { once: true });
      el.addEventListener('loadeddata', onReady, { once: true });
      // Perceptual budget: 600 ms is the upper bound for "feels instant".
      timer = setTimeout(tryPlay, 600);
      return () => {
        el.removeEventListener('canplay', onReady);
        el.removeEventListener('loadeddata', onReady);
        if (timer) clearTimeout(timer);
      };
    }
    const el = videoRef.current;
    if (el && !el.paused) el.pause();
    return undefined;
  }, [isActive, playbackUrl, ensurePlayback, playWithAutoplayFallback]);

  // ── Document Visibility — universal battery/bandwidth saver ─────────────
  // Pause when the tab/window is hidden, resume only if this slide is
  // still the active one. Saves device battery, bandwidth, and prevents
  // a video from "playing in the background" when the user has switched
  // away — same behaviour TikTok/Shorts ship.
  useEffect(() => {
    if (!isActive) return;
    const onVisibility = () => {
      const el = videoRef.current;
      if (!el) return;
      if (document.hidden) {
        if (!el.paused) el.pause();
      } else if (isActive && playbackUrl) {
        playWithAutoplayFallback(el);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isActive, playbackUrl, playWithAutoplayFallback]);

  // ── Keyboard-bus listeners (Space → toggle play, M → toggle mute) ───────
  // Only the active slide responds. We use window-level custom events to
  // avoid prop-drilling every keyboard handler through the slide contract.
  useEffect(() => {
    if (!isActive) return;
    const onTogglePlay = () => {
      const el = videoRef.current;
      if (!el || !playbackUrl) return;
      if (el.paused) el.play().catch(() => {});
      else el.pause();
    };
    const onToggleMute = () => setMuted((m) => {
      const next = !m;
      // User intent — persist for the rest of the session and future visits.
      writeMutedPref(next);
      return next;
    });
    window.addEventListener('video-feed:toggle-play', onTogglePlay);
    window.addEventListener('video-feed:toggle-mute', onToggleMute);
    return () => {
      window.removeEventListener('video-feed:toggle-play', onTogglePlay);
      window.removeEventListener('video-feed:toggle-mute', onToggleMute);
    };
  }, [isActive, playbackUrl]);

  // Native event wiring for buffering UI + progress bar.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const updateBuffered = () => {
      try {
        const dur = el.duration;
        if (!dur || !isFinite(dur) || dur <= 0) return;
        setDurationSec(dur);
        const ranges = el.buffered;
        let end = 0;
        for (let i = 0; i < ranges.length; i++) {
          if (ranges.end(i) > end) end = ranges.end(i);
        }
        setBufferedPct(Math.min(100, Math.round((end / dur) * 100)));
      } catch {}
    };

    const updateTime = () => {
      if (isScrubbing) return;
      const dur = el.duration;
      if (dur && isFinite(dur) && dur > 0) {
        setDurationSec(dur);
      }
      const t = el.currentTime;
      if (isFinite(t) && t >= 0) setCurrentTimeSec(t);
    };

    const onWaiting = () => {
      setBuffering(true);
      // Notify the bandwidth estimator so the next prewarm cycle
      // downshifts. This is the standard "panic mode" from
      // MPEG-DASH/HLS players: an observed stall is the single best
      // ground-truth signal we have that throughput is lower than the
      // EMA currently believes.
      notifyStall();
      emitFeedEvent({ kind: 'buffering_start', video_id: item.video_id, index });
    };
    const onCanPlay = () => setBuffering(false);
    const onPlaying = () => {
      setBuffering(false);
      setPlaying(true);
      emitFeedEvent({ kind: 'buffering_end', video_id: item.video_id, index });
    };
    const onPauseEvt = () => setPlaying(false);
    const onPlayEvt = () => {
      setPlaying(true);
      emitFeedEvent({ kind: 'play_start', video_id: item.video_id, index });
    };
    el.addEventListener('waiting', onWaiting);
    el.addEventListener('stalled', onWaiting);
    el.addEventListener('canplay', onCanPlay);
    el.addEventListener('playing', onPlaying);
    el.addEventListener('play', onPlayEvt);
    el.addEventListener('pause', onPauseEvt);
    el.addEventListener('progress', updateBuffered);
    el.addEventListener('loadedmetadata', updateBuffered);
    el.addEventListener('timeupdate', updateBuffered);
    el.addEventListener('loadedmetadata', updateTime);
    el.addEventListener('timeupdate', updateTime);
    el.addEventListener('durationchange', updateTime);
    return () => {
      el.removeEventListener('waiting', onWaiting);
      el.removeEventListener('stalled', onWaiting);
      el.removeEventListener('canplay', onCanPlay);
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('play', onPlayEvt);
      el.removeEventListener('pause', onPauseEvt);
      el.removeEventListener('progress', updateBuffered);
      el.removeEventListener('loadedmetadata', updateBuffered);
      el.removeEventListener('timeupdate', updateBuffered);
      el.removeEventListener('loadedmetadata', updateTime);
      el.removeEventListener('timeupdate', updateTime);
      el.removeEventListener('durationchange', updateTime);
    };
  }, [playbackUrl, item.video_id, index, isScrubbing]);

  const seekPct = durationSec > 0
    ? Math.min(100, Math.max(0, (currentTimeSec / durationSec) * 100))
    : 0;

  const applySeekPercent = useCallback((pct: number) => {
    const el = videoRef.current;
    if (!el) return;
    const dur = el.duration;
    if (!dur || !isFinite(dur) || dur <= 0) return;
    const clamped = Math.min(100, Math.max(0, pct));
    const t = (clamped / 100) * dur;
    el.currentTime = t;
    setCurrentTimeSec(t);
  }, []);

  const seekRailRef = useRef<HTMLDivElement | null>(null);

  const seekFromClientX = useCallback((clientX: number) => {
    const rail = seekRailRef.current;
    if (!rail) return;
    const rect = rail.getBoundingClientRect();
    if (rect.width <= 0) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    applySeekPercent(pct);
  }, [applySeekPercent]);

  useEffect(() => {
    if (!isScrubbing) return;
    const onPointerUp = () => setIsScrubbing(false);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [isScrubbing]);

  const togglePlay = useCallback(() => {
    const el = videoRef.current;
    if (!el || !playbackUrl) return;
    if (el.paused) el.play().catch(() => {});
    else el.pause();
  }, [playbackUrl]);

  const seekBy = useCallback((deltaSec: number) => {
    const el = videoRef.current;
    if (!el) return;
    const dur = el.duration;
    if (!dur || !isFinite(dur) || dur <= 0) return;
    const next = Math.max(0, Math.min(dur, (el.currentTime || 0) + deltaSec));
    el.currentTime = next;
    setCurrentTimeSec(next);
  }, []);

  // ── Controls overlay visibility (touch only) ───────────────────
  const showControlsTemporarily = useCallback(() => {
    setControlsVisible(true);
    if (controlsHideTimerRef.current) window.clearTimeout(controlsHideTimerRef.current);
    controlsHideTimerRef.current = window.setTimeout(() => {
      setControlsVisible(false);
    }, 3200);
  }, []);

  // While paused (and on-screen) keep the chrome up; auto-hide only when
  // the video is actually playing so the immersive feed stays clean.
  useEffect(() => {
    if (!isTouchDevice) return;
    if (!playing && isActive && playbackUrl) {
      if (controlsHideTimerRef.current) window.clearTimeout(controlsHideTimerRef.current);
      setControlsVisible(true);
    }
  }, [playing, isActive, playbackUrl, isTouchDevice]);

  useEffect(() => () => {
    if (tapSingleTimerRef.current) window.clearTimeout(tapSingleTimerRef.current);
    if (controlsHideTimerRef.current) window.clearTimeout(controlsHideTimerRef.current);
    if (seekHintTimerRef.current) window.clearTimeout(seekHintTimerRef.current);
  }, []);

  const handleSurfaceTap = useCallback((e: React.PointerEvent<HTMLVideoElement>) => {
    const now = Date.now();
    if (now < ignoreTapUntilRef.current || now < suppressOverlayUntilRef.current) return;

    if (!isTouchDevice) {
      togglePlay();
      return;
    }

    const dt = now - lastTapTsRef.current;
    lastTapTsRef.current = now;

    if (dt > 0 && dt < 260 && playbackUrl) {
      if (tapSingleTimerRef.current) {
        window.clearTimeout(tapSingleTimerRef.current);
        tapSingleTimerRef.current = null;
      }
      const rect = e.currentTarget.getBoundingClientRect();
      const isLeft = (e.clientX - rect.left) < rect.width / 2;
      const delta = isLeft ? -5 : 5;
      seekBy(delta);
      if (seekHintTimerRef.current) window.clearTimeout(seekHintTimerRef.current);
      setSeekHint({ side: isLeft ? 'left' : 'right', delta });
      seekHintTimerRef.current = window.setTimeout(() => setSeekHint(null), 520);
      showControlsTemporarily();
      return;
    }

    if (tapSingleTimerRef.current) window.clearTimeout(tapSingleTimerRef.current);
    tapSingleTimerRef.current = window.setTimeout(() => {
      if (controlsVisible) {
        setControlsVisible(false);
        if (controlsHideTimerRef.current) window.clearTimeout(controlsHideTimerRef.current);
      } else {
        showControlsTemporarily();
      }
      tapSingleTimerRef.current = null;
    }, 180);
  }, [isTouchDevice, playbackUrl, controlsVisible, togglePlay, showControlsTemporarily, seekBy]);

  const resetPinch = useCallback(() => {
    pinchDistanceRef.current = null;
    pinchScaleStartRef.current = 1;
    pinchScaleRef.current = 1;
    setPinchActive(false);
    setPinchScale(1);
  }, []);

  // ── Pinch-to-zoom on the video (non-passive native listeners) ──
  // React's synthetic touch handlers are PASSIVE — `preventDefault()` is a
  // no-op there, so the browser's page-level pinch-zoom would still fire,
  // shrinking the overlay chrome AND never snapping back (the exact bug we
  // saw). We attach raw listeners with `{ passive: false }` so we can fully
  // suppress the native gesture and own the zoom ourselves: a transient
  // transform that ALWAYS animates back to 1× the instant the gesture ends.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !isTouchDevice || !playbackUrl) return;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const t0 = e.touches[0];
        const t1 = e.touches[1];
        pinchDistanceRef.current = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
        pinchScaleStartRef.current = pinchScaleRef.current;
        setPinchActive(true);
        suppressOverlayUntilRef.current = Date.now() + 700;
        e.preventDefault();
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinchDistanceRef.current != null) {
        const t0 = e.touches[0];
        const t1 = e.touches[1];
        const dist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
        const raw = pinchScaleStartRef.current * (dist / pinchDistanceRef.current);
        const next = Math.max(1, Math.min(3, raw));
        pinchScaleRef.current = next;
        setPinchScale(next);
        e.preventDefault();
      }
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (pinchDistanceRef.current != null && e.touches.length < 2) {
        // Gesture finished — snap straight back to 1× (TikTok/IG behaviour).
        pinchDistanceRef.current = null;
        pinchScaleStartRef.current = 1;
        pinchScaleRef.current = 1;
        setPinchActive(false);
        setPinchScale(1);
        setControlsVisible(false);
        if (controlsHideTimerRef.current) window.clearTimeout(controlsHideTimerRef.current);
        if (tapSingleTimerRef.current) window.clearTimeout(tapSingleTimerRef.current);
        lastTapTsRef.current = 0;
        // A pinch release emits pointer/touch up events that can look like
        // taps to our gesture layer. Suppress any overlay behavior long enough
        // that only an intentional later tap can reopen controls.
        const nextBlock = Date.now() + 700;
        ignoreTapUntilRef.current = nextBlock;
        suppressOverlayUntilRef.current = nextBlock;
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: false });
    el.addEventListener('touchcancel', onTouchEnd, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [isTouchDevice, playbackUrl]);

  useEffect(() => {
    if (!isActive) resetPinch();
  }, [isActive, resetPinch]);

  return (
    <section
      ref={slideRef}
      className="relative h-full w-full snap-start snap-always flex items-center justify-center
                 bg-black"
      style={{ minHeight: '100%' }}
      aria-label={`Video ${item.display_name ?? item.scope_key}`}
    >
      {/* Video surface */}
      <div className="absolute inset-0 flex items-center justify-center">
        {playbackUrl ? (
          <video
            ref={videoRef}
            key={playbackUrl}
            src={playbackUrl}
            poster={posterUrl ?? undefined}
            playsInline
            loop
            muted={muted}
            preload={preload}
            onPointerUp={handleSurfaceTap}
            onError={() => {
              // Auto-recovery: one transparent re-sign + retry handles
              // signed-URL races and brief network glitches without ever
              // surfacing a broken-video chrome to the user. Bounded to
              // a single retry to avoid loops on genuinely dead assets.
              if (recoveryAttemptsRef.current >= 1) {
                emitFeedEvent({
                  kind: 'recovery_failed',
                  video_id: item.video_id,
                  index,
                });
                setError('Playback failed after retry');
                return;
              }
              recoveryAttemptsRef.current += 1;
              emitFeedEvent({
                kind: 'recovery_retry',
                video_id: item.video_id,
                index,
              });
              const stalePlaybackUrl = playbackUrl;
              setPlaybackUrl(null);
              requestPlayback(item.video_id)
                .then((res) => {
                  // Force a remount even if the URL came back identical
                  // by appending a cache-busting marker; the browser
                  // will re-issue the range request against a fresh
                  // socket.
                  const next = res.playback_url + (res.playback_url === stalePlaybackUrl ? `#retry=${Date.now()}` : '');
                  setPlaybackUrl(next);
                  setContentType(res.content_type || 'video/mp4');
                })
                .catch((err) => {
                  if (err instanceof PlaybackNotReadyError) {
                    // Mid-playback re-sign hit a not-ready window — likely
                    // a signed-URL expiry that raced with a backend
                    // republish. Fall through to the not-ready overlay
                    // and let the timer-driven retry path heal it.
                    setNotReady((prev) => ({
                      code: err.code,
                      attempt: (prev?.attempt ?? 0) + 1,
                      nextRetryAtMs: Date.now() + nextPlaybackRetryDelay(prev?.attempt ?? 0, err.retryAfterMs),
                    }));
                    if (notReadyRetryTimerRef.current !== null) {
                      window.clearTimeout(notReadyRetryTimerRef.current);
                    }
                    notReadyRetryTimerRef.current = window.setTimeout(() => {
                      notReadyRetryTimerRef.current = null;
                      void ensurePlayback();
                    }, nextPlaybackRetryDelay(0, err.retryAfterMs));
                    return;
                  }
                  emitFeedEvent({
                    kind: 'recovery_failed',
                    video_id: item.video_id,
                    index,
                    detail: { message: (err as Error)?.message },
                  });
                  setError((err as Error)?.message || 'Failed to recover playback');
                });
            }}
            className="max-h-full max-w-full object-contain cursor-pointer"
            style={{
              transform: `scale(${pinchScale})`,
              transformOrigin: 'center center',
              transition: pinchActive ? 'none' : 'transform 140ms ease-out',
              // pan-y lets the vertical snap-feed still scroll, while our
              // non-passive listeners own two-finger pinch. Combined with the
              // viewer's `user-scalable=no` viewport lock, browser page-zoom
              // can never shrink the overlay chrome.
              touchAction: isTouchDevice ? 'pan-y' : undefined,
            }}
          >
            <source src={playbackUrl} type={contentType} />
          </video>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 text-neutral-500">
            {/* Poster fallback — when we have a poster sidecar but the
                playback URL is still minting, show the poster as the
                background so the user never sees a black void. Cross-
                fades to the live video on `playbackUrl` resolution
                (the entire branch unmounts and the <video poster> takes
                over seamlessly). */}
            {!error && posterUrl && (
              <img
                src={posterUrl}
                alt=""
                aria-hidden
                draggable={false}
                className="absolute inset-0 w-full h-full object-contain opacity-90"
              />
            )}
            {error ? (
              <>
                <p className="text-rose-300 text-sm font-semibold">Cannot load video</p>
                <p className="text-[11px] text-neutral-500 max-w-md text-center px-4 break-words">{error}</p>
              </>
            ) : notReady ? (
              // ── Not-ready overlay (replaces "Cannot load video" for 404s)
              // The asset exists in metadata but its GCS object is
              // still being written. Calm chrome + jittered retry timer
              // means the user sees this card heal itself within seconds.
              <>
                <div className="absolute inset-8 rounded-2xl bg-white/[0.02] border border-white/5 animate-pulse pointer-events-none" />
                <div className="w-8 h-8 rounded-full border-2 border-neutral-700 border-t-neon_orange-300 animate-spin" />
                <p className="text-xs text-neutral-300">
                  {humanizePlaybackNotReady(notReady.code)}…
                </p>
                <p className="text-[10px] text-neutral-500">
                  {notReady.attempt >= PLAYBACK_NOT_READY_MAX_ATTEMPTS
                    ? 'Still finishing—pull to refresh in a moment'
                    : `Retrying automatically (attempt ${notReady.attempt}/${PLAYBACK_NOT_READY_MAX_ATTEMPTS})`}
                </p>
              </>
            ) : (
              <>
                {/* Skeleton-first paint: subtle PAC-Glass placeholder + ring
                    spinner. No black void, no jarring spinner-on-black. */}
                <div className="absolute inset-8 rounded-2xl bg-white/[0.02] border border-white/5 animate-pulse pointer-events-none" />
                <div className="w-8 h-8 rounded-full border-2 border-neutral-700 border-t-neon_orange-300 animate-spin" />
                <p className="text-xs">
                  {isActive ? 'Preparing…' : 'Scroll to play'}
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Buffering overlay */}
      {playbackUrl && buffering && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full
                          bg-black/70 border border-white/20 backdrop-blur-md">
            <div className="w-3.5 h-3.5 rounded-full border-2 border-neutral-500 border-t-neon_orange-300 animate-spin" />
            <span className="text-[11px] text-neutral-200">Buffering · {bufferedPct}%</span>
          </div>
        </div>
      )}

      {/* Center play/pause (touch overlay — Shorts/Reels pattern). Tapping the
          surface reveals chrome; this explicit button is the ONLY place that
          toggles playback, so a stray tap never pauses the video. */}
      {isTouchDevice && playbackUrl && (controlsVisible || !playing) && (
        <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
          <button
            type="button"
            aria-label={playing ? 'Pause' : 'Play'}
            onClick={(e) => {
              e.stopPropagation();
              togglePlay();
              showControlsTemporarily();
            }}
            className="pointer-events-auto flex items-center justify-center w-16 h-16
                       rounded-full bg-black/45 border border-white/20 backdrop-blur-md
                       text-white transition-transform active:scale-95"
          >
            {playing ? (
              <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current" aria-hidden>
                <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="w-9 h-9 fill-current translate-x-0.5" aria-hidden>
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
        </div>
      )}

      {seekHint && (
        <div className="absolute inset-0 z-20 pointer-events-none">
          <div
            className={[
              'absolute top-1/2 -translate-y-1/2',
              'text-white text-xl font-extrabold tracking-tight',
              '[text-shadow:0_2px_10px_rgba(0,0,0,0.9)] transition-opacity duration-150',
              seekHint.side === 'left' ? 'left-6' : 'right-6',
            ].join(' ')}
          >
            {seekHint.delta > 0 ? '+5s' : '-5s'}
          </div>
        </div>
      )}

      {/* Bottom progress / scrubber (mobile-safe): keep this clearly above
          browser chrome/home-indicator so it is always visible on iOS/Android. */}
      {playbackUrl && (
        <div
          className="absolute left-4 right-4 z-50"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 4.5rem)' }}
        >
          {(!isTouchDevice || controlsVisible || isScrubbing) && (
            <div className="mb-1 flex items-center justify-between px-0.5 text-[10px] font-medium text-white/70 tabular-nums">
              <span>{formatSeconds(currentTimeSec)}</span>
              <span>{formatSeconds(durationSec)}</span>
            </div>
          )}
          <div
            ref={seekRailRef}
            className="relative w-full flex items-center cursor-pointer touch-none select-none"
            style={{ height: 26 }}
            onPointerDown={(e) => {
              (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
              setIsScrubbing(true);
              seekFromClientX(e.clientX);
              showControlsTemporarily();
            }}
            onPointerMove={(e) => {
              if (!isScrubbing) return;
              seekFromClientX(e.clientX);
            }}
            onPointerUp={() => {
              setIsScrubbing(false);
              showControlsTemporarily();
            }}
            aria-label="Seek video"
            role="slider"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(seekPct)}
          >
            {/* Track base */}
            <div
              className={[
                'absolute left-0 right-0 rounded-full bg-white/35 transition-[height] duration-150',
                (controlsVisible || isScrubbing) ? 'h-[4px]' : 'h-[3px]',
              ].join(' ')}
              style={{ bottom: 8 }}
            />
            {/* Buffered */}
            <div
              className={[
                'absolute left-0 rounded-full bg-white/60 transition-[height] duration-150',
                (controlsVisible || isScrubbing) ? 'h-[4px]' : 'h-[3px]',
              ].join(' ')}
              style={{ width: `${bufferedPct}%`, bottom: 8 }}
            />
            {/* Played */}
            <div
              className={[
                'absolute left-0 rounded-full bg-neon_orange-300 transition-[height] duration-150',
                (controlsVisible || isScrubbing) ? 'h-[4px]' : 'h-[3px]',
              ].join(' ')}
              style={{ width: `${seekPct}%`, bottom: 8 }}
            />
            {/* Draggable handle — only while interacting (keeps it minimal) */}
            {(controlsVisible || isScrubbing) && (
              <div
                className="absolute w-3 h-3 rounded-full bg-white shadow-[0_0_0_2px_rgba(0,0,0,0.4)] transition-opacity duration-150"
                style={{ left: `calc(${seekPct}% - 6px)`, bottom: 4 }}
                aria-hidden
              />
            )}
            {/* Time bubble while scrubbing */}
            {isScrubbing && durationSec > 0 && (
              <div
                className="absolute -top-1 px-2 py-0.5 rounded-md text-[10px] font-medium
                           bg-black/80 text-white border border-white/15 backdrop-blur-sm pointer-events-none"
                style={{ left: `calc(${seekPct}% - 28px)` }}
              >
                {formatSeconds(currentTimeSec)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Right rail — actions (TikTok-style vertical stack) */}
      <div
        className={[
          'absolute flex flex-col items-center gap-3 z-10',
          'right-3 bottom-28 sm:bottom-32',
        ].join(' ')}
      >
        {!minimalChrome && item.geo && (
          <MapPinLink
            geo={item.geo}
            videoLabel={item.display_name ?? item.scope_key}
            size="md"
          />
        )}
        {(!isTouchDevice || controlsVisible) && (
          <ActionButton
            label={muted ? 'Unmute' : 'Mute'}
            onClick={() => {
              emitFeedEvent({
                kind: 'mute_toggle',
                video_id: item.video_id,
                detail: { source: 'button', next_state: muted ? 'unmuted' : 'muted' },
              });
              setMuted((m) => {
                const next = !m;
                writeMutedPref(next);
                return next;
              });
            }}
          >
            {muted ? (
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden>
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.59 3l2.7 2.71-1.42 1.41L15.17 13.4l-2.71 2.7-1.41-1.41 2.7-2.7-2.7-2.71 1.41-1.41 2.71 2.7 2.7-2.7 1.41 1.41-2.7 2.71z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden>
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 00-2.5-4.03v8.05A4.5 4.5 0 0016.5 12zm-2.5-7.71v2.06A7 7 0 0119 12a7 7 0 01-5 6.65v2.06A9 9 0 0021 12a9 9 0 00-7-7.71z" />
              </svg>
            )}
          </ActionButton>
        )}
        {!minimalChrome && !readOnly && (
          <ActionButton
            label={deleting ? 'Deleting…' : 'Delete'}
            onClick={() => !deleting && onRequestDelete(item)}
            tone="rose"
            disabled={deleting}
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden>
              <path d="M9 3h6l1 2h4v2H4V5h4l1-2zm-3 6h12l-1 12H7L6 9zm3 2v8h2v-8H9zm4 0v8h2v-8h-2z" />
            </svg>
          </ActionButton>
        )}

        {/* Per-video share — mints a public `/s/<token>` link pinned to
            THIS video (scope_key="video:<id>") instead of the whole
            gallery. Hidden in read-only mode (public viewers already
            have the link). Same component as the header gallery-wide
            share button — single source of truth for the share UX. */}
        {!minimalChrome && !readOnly && (
          <ShareLinkButton videoId={item.video_id} variant="rail" />
        )}

        {/* Download — always rendered; respects the user's playback URL
            (V4-signed, short-TTL). Rendered as an anchor styled like the
            ActionButton glass pill so it inherits the same transparent
            overlay treatment as the surrounding controls. */}
        {!minimalChrome && canDownload && playbackUrl && (
          <a
            href={playbackUrl}
            download={item.filename || item.display_name || `${item.scope_key}.${item.container || 'mp4'}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download video"
            title="Download"
            className="w-11 h-11 rounded-full flex items-center justify-center
                       bg-black/30 hover:bg-black/45 border border-white/15 hover:border-white/30
                       backdrop-blur-md text-white/90 transition-colors
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/40"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden>
              <path d="M5 20h14v-2H5v2zm7-18l-6 6h4v6h4V8h4l-6-6z" />
            </svg>
          </a>
        )}
      </div>
    </section>
  );
}

const FeedSlide = memo(FeedSlideImpl);

function ActionButton({
  children,
  label,
  onClick,
  disabled,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'neutral' | 'rose';
}) {
  const toneClasses =
    tone === 'rose'
      ? 'border-rose-400/40 text-rose-200 hover:bg-rose-500/30 hover:border-rose-300/70'
      : 'border-white/15 text-neutral-100 hover:bg-white/10 hover:border-white/30';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={[
        'w-11 h-11 flex items-center justify-center rounded-full backdrop-blur-md',
        'bg-black/30 border transition-all duration-150',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/60',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        toneClasses,
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function formatSeconds(v: number): string {
  if (!Number.isFinite(v) || v < 0) return '0:00';
  const total = Math.floor(v);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
